/* global axios */
// store/modules/promotions.js

const knowledgeProxy = (accountId, path) =>
  `/api/v1/accounts/${accountId}/proxy/knowledge${path}`;

function handleApiError(error) {
  console.error('API Error:', {
    status: error.response?.status,
    data: error.response?.data,
    message: error.message
  });
  throw error.response?.data || error;
}

export default {
  namespaced: true,

  state: {
    list: [],
    loading: false,
    pagination: {
      page: 1,
      pageSize: 20,
      total: 0,
      totalPages: 0
    }
  },

  getters: {
    all(state) { return state.list; },
    byId: (state) => (id) => state.list.find(p => p.id === id),
    activePromotions(state) { return state.list.filter(p => p.is_active); },
    inactivePromotions(state) { return state.list.filter(p => !p.is_active); },
    forBranch: (state) => (branchId) => {
      return state.list.filter(p =>
        p.assignments?.some(a => a.branch_id === branchId || a.branch?.id === branchId)
      );
    },
    forService: (state) => (branchId, serviceId) => {
      return state.list.filter(p =>
        p.assignments?.some(a =>
          (a.branch_id === branchId || a.branch?.id === branchId) &&
          a.service_ids?.includes(serviceId) ||
          a.services?.some(s => s.id === serviceId)
        )
      );
    },
    isLoading(state) { return state.loading; },
    pagination(state) { return state.pagination; }
  },

  mutations: {
    SET_LOADING(state, status) { state.loading = status; },
    SET_LIST(state, { items, pagination }) {
      state.list = items;
      state.pagination = { ...state.pagination, ...pagination };
    },
    ADD_PROMOTION(state, promotion) { state.list.push(promotion); },
    UPDATE_PROMOTION(state, { id, data }) {
      const index = state.list.findIndex(p => p.id === id);
      if (index !== -1) {
        state.list.splice(index, 1, { ...state.list[index], ...data });
      }
    },
    DELETE_PROMOTION(state, id) { state.list = state.list.filter(p => p.id !== id); },
    CLEAR_LIST(state) {
      state.list = [];
      state.pagination = { page: 1, pageSize: 20, total: 0, totalPages: 0 };
    }
  },

  actions: {
    async fetchPromotions({ commit }, {
      accountId,
      isActive = null,
      branchId = null,
      serviceId = null,
      search = null,
      page = 1,
      pageSize = 20
    }) {
      commit('SET_LOADING', true);

      try {
        const params = { page, page_size: pageSize };
        if (isActive != null) params.is_active = isActive;
        if (branchId) params.branch_id = branchId;
        if (serviceId) params.service_id = serviceId;
        if (search) params.search = search;

        const response = await axios.get(
          knowledgeProxy(accountId, `/v2/accounts/${accountId}/promotions`),
          { params }
        );
        
        const { items, total, page: currentPage, page_size, total_pages } = response.data;
        
        commit('SET_LIST', {
          items,
          pagination: { page: currentPage, pageSize: page_size, total, totalPages: total_pages }
        });
        
        return response.data;
      } catch (error) {
        handleApiError(error);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async createPromotion({ commit }, { accountId, promotionData }) {
      commit('SET_LOADING', true);
      
      try {
        const apiPayload = {
          promotion_name: promotionData.promotion_name,
          description: promotionData.description || '',
          tnc: promotionData.tnc || '',
          expired_date: promotionData.expired_date,
          price: promotionData.price,
          is_active: promotionData.is_active ?? true,
          assignments: promotionData.assignments?.map(a => ({
            branch_id: a.branch_id || a.branch?.id,
            service_ids: a.service_ids || a.services?.map(s => s.id) || []
          })) || []
        };

        const response = await axios.post(
          knowledgeProxy(accountId, `/v2/accounts/${accountId}/promotions`),
          apiPayload
        );
        
        return response.data;
      } catch (error) {
        handleApiError(error);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchPromotionById({ commit }, { accountId, promotionId }) {
      commit('SET_LOADING', true);
      
      try {
        const response = await axios.get(
          knowledgeProxy(accountId, `/v2/accounts/${accountId}/promotions/${promotionId}`)
        );
        return response.data;
      } catch (error) {
        handleApiError(error);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async updatePromotion({ commit }, { accountId, promotionId, data }) {
      commit('SET_LOADING', true);

      try {
        const apiPayload = { ...data };
        if (data.assignments) {
          apiPayload.assignments = data.assignments.map(a => ({
            branch_id: a.branch_id || a.branch?.id,
            service_ids: a.service_ids || a.services?.map(s => s.id) || []
          }));
        }

        const response = await axios.put(
          knowledgeProxy(accountId, `/v2/accounts/${accountId}/promotions/${promotionId}`),
          apiPayload
        );
        return response.data;
      } catch (error) {
        handleApiError(error);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async togglePromotionStatus({ dispatch }, { accountId, promotionId, isActive }) {
      return await dispatch('updatePromotion', {
        accountId,
        promotionId,
        data: { is_active: isActive }
      });
    },

    async deletePromotion({ commit }, { accountId, promotionId }) {
      commit('SET_LOADING', true);
      
      try {
        await axios.delete(
          knowledgeProxy(accountId, `/v2/accounts/${accountId}/promotions/${promotionId}`)
        );
        return true;
      } catch (error) {
        handleApiError(error);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async getPromotionsForServiceInBranch({ dispatch }, { accountId, branchId, serviceId }) {
      console.log('[API] getPromotionsForServiceInBranch:', { accountId, branchId, serviceId });
      
      const result = await dispatch('fetchPromotions', {
        accountId,
        branchId,
        serviceId,
        pageSize: 100
      });
      
      return result?.items || [];
    },

    async getPromotionsWithBranchesForService({ dispatch }, { accountId, serviceId }) {
      const result = await dispatch('fetchPromotions', {
        accountId,
        serviceId,
        pageSize: 100
      });
      
      const promotions = result?.items || [];
      return promotions.map(promotion => ({
        ...promotion,
        branches: promotion.assignments?.map(a => ({
          id: a.branch_id || a.branch?.id,
          name: a.branch?.name || 'Unknown Branch'
        })) || []
      }));
    },

    debugPromotionsData({ state }) {
      console.log('[DEBUG] Current promotions state:', {
        total: state.list.length,
        loading: state.loading,
        pagination: state.pagination,
        promotions: state.list.map(p => ({
          id: p.id,
          name: p.promotion_name,
          assignments: p.assignments?.map(a => ({
            branchId: a.branch_id || a.branch?.id,
            serviceIds: a.service_ids || a.services?.map(s => s.id)
          }))
        }))
      });
    },

    clearPromotions({ commit }) {
      commit('CLEAR_LIST');
    }
  }
};
